import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { listOtherSaleProducts } from '../api/otherSales';
import { cancelNativeSale, completePaidNativeSale, declareNativeSaleUnpaid, getNativeSale, listPendingNativeSales, openNativeSale, payNativeSale } from '../api/nativeSales';
import {
  reportPaymentObservation,
  startPaymentAttempt,
  type PaymentObservationInput,
  type PaymentObservationResponse,
} from '../api/paymentAttempts';
import { ApiError, getErrorMessage } from '../api/client';
import {
  chargeWithZettle,
  checkZettleAvailability,
  checkZettleReaderPermissions,
  ensureZettleReaderPermissions,
  onZettleAuthChange,
  openZettleAppSettings,
  readZettleSessionIdentity,
  retrieveWithZettle,
  startZettleLogin,
  type ZettleAvailability,
} from '../native/zettlePayments';
import {
  NOT_LAUNCHED_OBSERVATION,
  READER_SALE_CARD_UNRESOLVED_NOTICE,
  READER_SALE_IN_DOUBT_NOTICE,
  READER_SALE_PAID_NOTICE,
  READER_SALE_PAID_UNREAD_NOTICE,
  READER_SALE_SETTLED_NOTICE,
  READER_SALE_STILL_IN_DOUBT_NOTICE,
  RECOVERY_OBSERVATION,
  ZETTLE_LOGIN_OPENED_NOTICE,
  classifySaleCardTruth,
  createZettleSessionRefresher,
  decideZettleStep,
  describeZettleDeviceSessionBlock,
  describeZettleMethodHint,
  describePaidSaleClosed,
  describeRecoverableCardRisk,
  describeZettleOutcome,
  describeZettlePreflightBlock,
  forgetZettleLaunch,
  hasReaderPaymentInFlight,
  hasUnsettledCardPayment,
  normalizeZettleAvailability,
  noticeAfterZettleReaderPermissionCheck,
  noticeAfterZettleSessionCheck,
  observationFromBridge,
  readZettleLaunch,
  rememberZettleLaunch,
  startZettleAttemptAfterPreflight,
  watchReaderSale,
  zettleSessionAttestation,
  type SaleCardTruth,
  type ZettleNotice,
} from '../utils/zettlePos';
import { resolveStoreTimezone } from '../utils/timezones';
import { randomUuid } from '../utils/randomUuid';
import { useAuth } from '../auth/AuthProvider';
import { canDeclareNativeSaleUnpaid, canManageOtherSaleProducts, canSeeActivationGuide, getStoreScopeOptions } from '../auth/permissions';
import { useFirstSale } from '../utils/useFirstSale';
import { useAndroidBackClose } from '../android/useAndroidBackClose';
import { useSoftKeyboardFromShell } from '../layouts/softKeyboard';
import { Button, Dialog, EmptyPanel, ErrorPanel, Field, LoadingPanel, Textarea } from './ui/primitives';
import { PosCatalog, PosHeader, PosViewLinks, type PosViewLink } from './pos/PosCatalog';
import { PosCheckout } from './pos/PosCheckout';
import { PosDone } from './pos/PosDone';
import { PosOrderBar, PosOrderEstimate, PosOrderFoot, PosOrderLines } from './pos/PosOrder';
import { PosNotice } from './pos/PosParts';
import { PosRecovery } from './pos/PosRecovery';
import { scrollParentOf } from './pos/scrollParent';
import { useFitsSplit, useRoomBelow } from './pos/useFitsSplit';
import type { OtherSaleProduct } from '../types/otherSales';
import type { NativePaymentMethod, NativeSale } from '../types/nativeSales';
import type { Store } from '../types/stores';
import { formatMoney } from '../utils/costFormatting';
import {
  DECLARE_UNPAID_BASIS_SUGGESTION,
  describeCanceledSale,
  describeDeclareUnpaid,
  describeSaleOutcome,
  shortTime,
} from '../utils/nativePos';
import {
  addProductToCart,
  buildCheckoutLines,
  calculateCartPreview,
  cartItemCount,
  changeQuantity,
  classifyCheckoutError,
  describePaidCompletionError,
  deviceSessionBlockMessage,
  isCartEmpty,
  posCatalogState,
  posStoreState,
  removeFromCart,
  resolveEffectiveStoreId,
  saysPaymentConfirmed,
  serverAnswered,
  shouldClearCartOnCancel,
  type CartLine,
} from '../utils/otherSalePos';
import {
  POS_CARD_STEP,
  POS_CASH,
  autoPicksExternalCard,
  cashCovers,
  chooseCardPath,
  chooseMethod,
  describeCashReceived,
  describeUnits,
  posCatalogViewKey,
  readPosCatalogView,
  writePosCatalogView,
  type PosCardPath,
  type PosCashReceived,
  type PosCatalogView,
  type PosPaymentChoice,
} from '../utils/posView';
import { watchPosBrowserBack } from '../utils/posBrowserBack';
import { posOrderMemoryKey, recallPosOrder, rememberPosOrder } from '../utils/posOrderMemory';

/**
 * Rediseño · Bloque D: este componente es el punto de venta ENTERO —la lógica de la venta, del cobro
 * y de su recuperación— y decide cuál de sus tres pantallas se ve: venta (P-025), cobro (P-027) o
 * venta completada (P-028). Lo que se DIBUJA vive en `components/pos/`; aquí quedan, sin cambios, los
 * manejadores que hablan con el servidor y con el lector, y el orden en que lo hacen.
 *
 * Mini-POS de Ventas Otros. Native POS Lifecycle: el flujo ya no es "un botón que hace todo", sino
 * el ciclo real de un mostrador:
 *
 *   productos → carrito → [Cobrar] → venta guardada → método → [Confirmar cobro] → Listo
 *
 * Al tocar "Cobrar" la venta queda GUARDADA en el servidor antes de cobrar nada. Si el teléfono se
 * bloquea, la app se cierra o se va el internet, la venta se recupera y se puede continuar o
 * cancelar: nada se pierde y nada se cobra dos veces. Mientras está pendiente no descuenta
 * inventario ni cuenta como venta del día.
 *
 * El servidor calcula precio/IVA/totales desde el catálogo; los importes del carrito son estimación
 * hasta que la venta se guarda. Disponible para todo rol que pueda registrar ventas (el backend es
 * la autoridad final).
 */
export function OtherSalePos({ stores, storesLoaded, storesFailed, onSaleCreated, onOpenDetail, onGoToHistory, onGoToCatalog, viewLinks = NO_VIEW_LINKS, loyverseTip }: {
  stores: Store[];
  /** Deuda (m): ¿la consulta de tiendas ya terminó? Sin esto, `stores = []` se lee como «sin tienda asignada». */
  storesLoaded: boolean;
  /** Deuda (m): ¿y terminó MAL? Una carga fallida tampoco es «no tienes tiendas». */
  storesFailed: boolean;
  onSaleCreated: () => void;
  onOpenDetail: (saleId: string) => void;
  onGoToHistory: () => void;
  onGoToCatalog: () => void;
  /**
   * Rediseño · Bloque D: las otras vistas de la sección (Historial, Catálogo de productos, Consumo y
   * margen), ya filtradas por lo que esta persona puede abrir. La página deja de dibujar sus pestañas
   * mientras se vende; el punto de venta las ofrece con su propia piel.
   */
  viewLinks?: PosViewLink[];
  /** El aviso de «no dupliques lo que ya está en Loyverse», cuando la página dice que toca mostrarlo. */
  loyverseTip?: { onDismiss: () => void };
}) {
  const { user, business } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE';
  const canManageCatalog = canManageOtherSaleProducts(user?.role);
  const scope = useMemo(() => getStoreScopeOptions(user?.role, stores), [user?.role, stores]);

  // P-082: el pedido que esta persona dejó al salir del punto de venta, para retomarlo (`utils/posOrderMemory.ts`).
  const orderMemoryKey = posOrderMemoryKey(business?.id ?? user?.businessId, user?.id);
  const [recalled] = useState(() => recallPosOrder(orderMemoryKey));
  const [storeId, setStoreId] = useState<string>(() => recalled?.storeId ?? scope.defaultStoreId ?? stores[0]?.id ?? '');
  const [products, setProducts] = useState<OtherSaleProduct[]>();
  const [productsError, setProductsError] = useState<string>();
  const [cart, setCart] = useState<CartLine[]>(() => recalled?.cart ?? []);
  // PayPal/Zettle Entrega 1: 'CARD_READER' es la opción de cobro con lector (solo app Android con
  // el puente disponible). NO es un método manual: dispara el flujo integrado, no payNativeSale.
  // B0-03: 'CARD_UNSET' es «tocó Tarjeta y todavía no dijo cómo se cobra». Tampoco viaja: mientras
  // dure, `confirmPayment` no registra nada (ver `PosPaymentChoice`).
  const [paymentMethod, setPaymentMethod] = useState<PosPaymentChoice>('CASH');
  const [isSaving, setIsSaving] = useState(false);
  const [checkoutError, setCheckoutError] = useState<{ message: string; ambiguous: boolean }>();
  const [lastSale, setLastSale] = useState<NativeSale>();
  // Venta ya guardada en el servidor y pendiente de cobro: mientras exista, el POS muestra la
  // pantalla de cobro en lugar del catálogo (una sola cosa a la vez, pensado para una mano).
  const [pendingSale, setPendingSale] = useState<NativeSale>();
  // Ventas que quedaron a medias en esta tienda (recarga, cierre de app, pérdida de conexión).
  const [recoverable, setRecoverable] = useState<NativeSale[]>([]);
  // Clave del COBRO: se genera al entrar a la pantalla de cobro y se reutiliza en un reintento, para
  // que el servidor reconozca el intento y jamás cobre dos veces.
  const paymentKeyRef = useRef<string>();
  // PayPal/Zettle Entrega 1 — estado del cobro con lector:
  // · disponible: solo dentro de la app Android con el SDK inicializado (la web nunca lo muestra);
  // · intento en duda: bloquea "confirmar/cancelar" y ofrece "Verificar cobro" por la MISMA referencia;
  // · observación sin reportar: lo que el SDK devolvió y aún no llega al servidor (se reintenta, es
  //   idempotente) — jamás se descarta, porque puede ser un cobro ya realizado.
  // PAYPAL-GATE-02: disponibilidad Y modo (DEV / REAL) que decidió el build de Android. La web solo
  // lo lee para avisar al operador; no hay forma de pedir otro modo desde aquí.
  const [zettleAvailability, setZettleAvailability] = useState<ZettleAvailability>(() => normalizeZettleAvailability(undefined));
  // P-080: ¿ya se preguntó si este equipo tiene lector? Hasta saberlo, «no hay lector» no es un dato:
  // la terminal de fuera no se elige sola mientras pueda haber dos caminos.
  const [zettleChecked, setZettleChecked] = useState(false);
  // P-080 (D-P080-CARD-AUTOSELECT): la venta para la que la PANTALLA eligió la terminal de fuera, por ser
  // el único camino. Si aparece el lector deja de ser el único y esa elección ya no vale.
  const [cardAutoSaleId, setCardAutoSaleId] = useState<string>();
  // P-080 (D-P080-CASH-RECEIVED-VISUAL): lo escrito en «Recibí», atado a su venta. Solo pantalla: no se
  // guarda ni viaja. Vive aquí, y no en la pantalla de cobro, porque `confirmPayment` lo vigila.
  const [cashText, setCashText] = useState<{ saleId: string; text: string }>();
  // P-081-C1 (D-P081-C1-NO-DEFAULT-METHOD): la venta para la que la persona YA tocó cómo le pagan. El cobro abre sin
  // ningún método elegido —Efectivo ya no viene elegido—, y mientras no se elija, «Confirmar cobro» no registra nada.
  // `paymentMethod` conserva su valor de arranque, pero sin esta marca no se ve ni se cobra.
  const [pickedSaleId, setPickedSaleId] = useState<string>();
  // P-081: en qué nivel del cobro se está, para el Atrás: «¿Cómo te pagan?» (los métodos) o el panel de un método
  // ya tocado. Es solo de la pantalla: no cambia qué está elegido. `revealTick` pide traer el panel a la vista.
  const [payLevel, setPayLevel] = useState<'methods' | 'detail'>('methods');
  const [revealTick, setRevealTick] = useState(0);
  // P-081: la venta que en ESTA pantalla ya intentó cobrarse con el lector. El veredicto del servidor (cardTruth) no se
  // vuelve a leer tras un intento cancelado, así que no basta para saber que pasó por el lector: Atrás no la saca.
  const [readerTriedSaleId, setReaderTriedSaleId] = useState<string>();
  const [zettleNotice, setZettleNotice] = useState<ZettleNotice>();
  const [zettlePendingAttemptId, setZettlePendingAttemptId] = useState<string>();
  // Deuda (k): cancelar tiene que VERSE. Estado propio y no `lastSale`: ese banner está hecho para una
  // venta COBRADA (✓ verde, «Ver detalle»), y el de recuperables solo se pinta con `!lastSale`, así que
  // reutilizarlo escondería las otras ventas pendientes.
  const [canceledNotice, setCanceledNotice] = useState<{ title: string; detail: string; totalAmount: string }>();
  const [isCanceling, setIsCanceling] = useState(false);
  // Deuda (q): el veredicto del servidor sobre la venta abierta, SIEMPRE atado a su saleId — así el
  // veredicto de la venta anterior no puede decidir los botones de la siguiente.
  // FI-53: `unread` = el servidor confirmó el cobro y la venta no se pudo leer; de ella no se afirma nada.
  const [cardTruth, setCardTruth] = useState<{ saleId: string; truth: SaleCardTruth; unread?: true }>();
  // La fila del banner que está preguntando al servidor.
  const [resumingSaleId, setResumingSaleId] = useState<string>();
  // Deuda (t): al volver del lector el botón quedaba apagado 8-12 s sin girador ni texto. Eso es lo
  // que se percibe como «lento» (verificado en el video del 2026-09-29 23:02).
  const [confirmingWithServer, setConfirmingWithServer] = useState(false);
  // Deuda (w): tras un intento con tarjeta que no cobró, la persona debe volver a ELEGIR el método.
  // Sin esto la pantalla quedaba a un toque de registrar efectivo con la respuesta ya dada por hecha.
  const [mustPickMethod, setMustPickMethod] = useState(false);
  /**
   * Lote 5: la venta que el DUEÑO está cerrando por declaración, y el fundamento que escribe. El
   * fundamento arranca con una sugerencia editable, no con una afirmación de la app: lo que quede
   * escrito es lo que él declara, y se guarda con su declaración.
   */
  const [declaring, setDeclaring] = useState<NativeSale>();
  // Arranca VACÍO a propósito: con el fundamento prerrellenado el botón de confirmar nacía
  // habilitado y se podía declarar sin escribir nada. La sugerencia se muestra como `placeholder`.
  const [declarationBasis, setDeclarationBasis] = useState('');
  const [declarationError, setDeclarationError] = useState<string>();
  const [isDeclaring, setIsDeclaring] = useState(false);
  // Deuda (k): la venta que salió de ESTE carrito. Solo entonces cancelar puede vaciarlo.
  const cartSaleIdRef = useRef<string | undefined>(recalled?.cartSaleId);
  const pendingObservationRef = useRef<{ attemptId: string; body: PaymentObservationInput }>();
  // P-015: la venta que ya se cerró en ESTA pantalla. El vigilante del servidor y «Verificar cobro» pueden
  // enterarse a la vez de que el cobro quedó confirmado: el ciclo se cierra UNA sola vez (no se vacía dos
  // veces el carrito ni se avisa un error de una venta que ya está completada).
  const finishedSaleIdRef = useRef<string>();
  // Tienda que el usuario intenta elegir con el carrito no vacío — pendiente de confirmar en un
  // modal de la app (NO window.confirm: un diálogo nativo dentro del onChange de un <select>
  // controlado lo desincroniza y captura el foco del teclado del resto de la app).
  const [pendingStoreId, setPendingStoreId] = useState<string>();
  // Fase 21C-4C: guardia de condición de carrera. Cada carga captura un número de secuencia; solo la
  // más reciente puede escribir el estado, así una respuesta (o error) tardía de una tienda anterior
  // nunca reemplaza los productos de la tienda actual.
  const loadSeqRef = useRef(0);
  // Fase 21C-5: clave de idempotencia por INTENCIÓN de venta. Se genera al confirmar, se reutiliza en
  // un reintento seguro tras error ambiguo (mismo carrito → el servidor devuelve la misma venta), y
  // se invalida al modificar el carrito/tienda o al concretar la venta (nueva intención = nueva clave).
  const idempotencyKeyRef = useRef<string | undefined>(recalled?.idempotencyKey);
  // P-080: «Cobrar» está abriendo una venta (candado síncrono; ver `startCheckout`).
  const openingRef = useRef(false);

  // P0 (pre-client-ux-hardening): `stores` llega asincrónicamente; el initializer de `storeId` pudo
  // correr con stores=[] y dejar '' congelado — el POS afirmaba "catálogo vacío" con productos
  // reales. Cuando stores llega/cambia se resuelve la tienda efectiva SIN pisar una selección
  // válida del usuario (resolveEffectiveStoreId devuelve la actual si sigue existiendo).
  useEffect(() => {
    const next = resolveEffectiveStoreId(storeId, stores, scope.defaultStoreId);
    if (next === storeId) return;
    // P-082: un pedido de una tienda que ya no está a mano (p. ej. el que se retomó al volver) no se pasa a otra
    // tienda: se descarta con su intención, igual que al cambiar de tienda.
    if (storeId) {
      setCart([]);
      idempotencyKeyRef.current = undefined;
      cartSaleIdRef.current = undefined;
    }
    setStoreId(next);
  }, [stores, scope.defaultStoreId, storeId]);

  // P-082: al salir del punto de venta, el pedido se queda para cuando se vuelva (`utils/posOrderMemory.ts`): las
  // líneas, la tienda, la clave de intención y la venta que salió de él. Vacío, no se guarda nada.
  const orderNow = useRef({ cart, storeId });
  orderNow.current = { cart, storeId };
  useEffect(() => () => {
    if (!orderMemoryKey) return;
    rememberPosOrder({ key: orderMemoryKey, storeId: orderNow.current.storeId, cart: orderNow.current.cart, idempotencyKey: idempotencyKeyRef.current, cartSaleId: cartSaleIdRef.current });
  }, [orderMemoryKey]);

  const loadProducts = useCallback(async (activeStoreId: string) => {
    const seq = ++loadSeqRef.current;
    // Tienda aún sin resolver: estado de CARGA, nunca un "[]" que se disfrace de catálogo vacío.
    if (!activeStoreId) { setProducts(undefined); setProductsError(undefined); return; }
    setProducts(undefined);
    setProductsError(undefined);
    try {
      // Sin includeInactive: el mini-POS solo vende productos activos disponibles en la tienda.
      const result = await listOtherSaleProducts({ storeId: activeStoreId });
      if (loadSeqRef.current !== seq) return; // respuesta obsoleta: hay una carga más reciente en curso.
      setProducts(result.items);
    } catch (loadError) {
      if (loadSeqRef.current !== seq) return; // error de una carga vieja: no pisa un resultado más nuevo.
      // No convertir un fallo de carga en "sin productos": se deja el estado de error (con reintento)
      // y products en undefined para no mostrar el estado vacío ni productos de otra tienda.
      setProductsError(getErrorMessage(loadError, 'No fue posible cargar los productos.'));
    }
  }, []);

  useEffect(() => { void loadProducts(storeId); }, [storeId, loadProducts]);

  // Cobro con lector: se consulta al abrir el POS. Fuera de Android responde false sin tocar nada.
  // P-015: además se VUELVE A CONSULTAR (solo consultar — de aquí no sale ningún login ni cobro) cuando
  // la app regresa al frente y cuando el lado nativo avisa que la sesión del SDK cambió, para que el
  // operador vea la sesión iniciada sin reabrir la app. Disparos simultáneos = una sola consulta.
  useEffect(() => {
    let active = true;
    let lastAvailability = normalizeZettleAvailability(undefined);
    const refresh = createZettleSessionRefresher(checkZettleAvailability, (availability) => {
      if (!active) return;
      lastAvailability = availability;
      setZettleAvailability(availability);
      setZettleChecked(true);
      setZettleNotice((current) => noticeAfterZettleSessionCheck(current, availability));
    });
    // Deuda (l): al volver de los Ajustes de Android el permiso puede estar ya concedido, y hasta
    // P-021 nada podía corregir el aviso rojo. Se vuelve a CONSULTAR el estado (lectura; jamás una
    // solicitud, que abriría un diálogo en cada regreso). Solo puede reemplazar un aviso de permisos.
    const refreshAll = async () => {
      await refresh();
      if (!active) return;
      const permission = await checkZettleReaderPermissions();
      if (!active) return;
      setZettleNotice((current) => noticeAfterZettleReaderPermissionCheck(current, lastAvailability, permission));
    };
    void refreshAll();
    if (!Capacitor.isNativePlatform()) return () => { active = false; };
    const stopAuthListener = onZettleAuthChange(() => { void refreshAll(); });
    const resumeListener = CapacitorApp.addListener('resume', () => { void refreshAll(); });
    return () => {
      active = false;
      stopAuthListener();
      void resumeListener.then((listener) => listener.remove()).catch(() => undefined);
    };
  }, []);

  // Cualquier cambio del carrito/tienda invalida la intención anterior: la próxima confirmación
  // usará una clave nueva (no se puede reutilizar una clave para una venta distinta).
  const invalidateIntent = () => {
    idempotencyKeyRef.current = undefined;
    // Deuda (k): si el carrito cambió, ya no es el carrito de aquella venta.
    cartSaleIdRef.current = undefined;
  };

  const applyStoreChange = (nextStoreId: string) => {
    // Al cambiar de tienda se vacía el carrito para no mezclar productos de tiendas distintas.
    setCart([]);
    setCanceledNotice(undefined);
    setCheckoutError(undefined);
    setLastSale(undefined);
    setPendingStoreId(undefined);
    invalidateIntent();
    setStoreId(nextStoreId);
  };

  const handleStoreSelect = (nextStoreId: string) => {
    if (nextStoreId === storeId) return;
    if (isCartEmpty(cart)) {
      applyStoreChange(nextStoreId);
    } else {
      // No se cambia la tienda todavía: se guarda como pendiente y se pide confirmación en un modal.
      // El <select> sigue atado a `storeId`, y como esto es un cambio de estado, React re-renderiza
      // y el select vuelve a mostrar la tienda actual (nunca queda desincronizado).
      setPendingStoreId(nextStoreId);
    }
  };

  // Agregar/incrementar = empezar (o seguir) una venta nueva → se limpia el banner de éxito anterior.
  // Todo cambio del carrito invalida la intención (regenera la clave en la próxima confirmación).
  const addToCart = (product: OtherSaleProduct) => { setLastSale(undefined); setCanceledNotice(undefined); invalidateIntent(); setCart((current) => addProductToCart(current, product)); };
  const incLine = (productId: string) => { setLastSale(undefined); setCanceledNotice(undefined); invalidateIntent(); setCart((current) => changeQuantity(current, productId, 1)); };
  const decLine = (productId: string) => { setCanceledNotice(undefined); invalidateIntent(); setCart((current) => changeQuantity(current, productId, -1)); };
  const removeLine = (productId: string) => { setCanceledNotice(undefined); invalidateIntent(); setCart((current) => removeFromCart(current, productId)); };
  // Rediseño · Bloque D: «Vaciar» es quitar todas las líneas de una vez. Como cualquier otro cambio
  // del pedido, invalida la intención: la próxima venta usará una clave nueva.
  const clearCart = () => { setCanceledNotice(undefined); invalidateIntent(); setCart([]); };

  // B0-03: el primer nivel son cuatro métodos. Tocar «Tarjeta» NO elige nada cobrable —abre el
  // segundo paso—; elegir cualquier otro, o un camino de tarjeta, sí. Elegir es lo único que apaga la
  // obligación de volver a elegir: nunca se da por hecho cómo pagó el cliente.
  //
  // P-080 (D-P080-CARD-AUTOSELECT): si «Tarjeta» solo tiene un camino —la terminal de fuera, porque este
  // equipo no tiene lector— ese camino se elige solo y se ve elegido; «Confirmar cobro» sigue pidiendo su
  // toque. Se decide con `mustPickMethod` ANTES de limpiarlo: tras un intento con lector que no cobró,
  // nada se elige solo. El lector no se elige solo nunca.
  const pickMethod = (method: NativePaymentMethod) => {
    const next = chooseMethod(method, paymentMethod, mustPickMethod);
    const auto = autoPicksExternalCard(next, { readerOffered: zettleAvailability.available, readerChecked: zettleChecked, repick: mustPickMethod });
    setPaymentMethod(auto ? 'CARD' : next);
    if (auto) setCardAutoSaleId(pendingSale?.id);
    else if (next !== paymentMethod) setCardAutoSaleId(undefined);
    setMustPickMethod(false);
    setPickedSaleId(pendingSale?.id);
    enterMethodDetail();
  };
  const pickCardPath = (path: PosCardPath) => { setMustPickMethod(false); setCardAutoSaleId(undefined); setPaymentMethod(chooseCardPath(path)); setPickedSaleId(pendingSale?.id); enterMethodDetail(); };
  /** P-081-C1: ¿la persona ya eligió cómo le pagan esta venta? Hasta entonces nada se ve elegido ni se cobra. */
  const methodPicked = pendingSale !== undefined && pickedSaleId === pendingSale.id;
  /**
   * P-081: tocar un método —también el que ya estaba elegido— entra a su nivel (el panel del método) y lo trae
   * a la vista. Con Efectivo, «Total a recibir», «Recibí» y «Cambio» quedan juntos sobre «Confirmar cobro»;
   * el teclado no se abre solo (pagar exacto es Efectivo → Confirmar). Desde aquí, Atrás vuelve a «¿Cómo te pagan?».
   */
  const enterMethodDetail = () => { setPayLevel('detail'); setRevealTick((tick) => tick + 1); setBackBlocked(false); };
  /** La terminal de fuera la eligió la pantalla para esta venta y ya no es el único camino: apareció el lector. */
  const cardAutoStale = (): boolean => paymentMethod === 'CARD' && pendingSale !== undefined && cardAutoSaleId === pendingSale.id && zettleAvailability.available;
  // Y esa elección se RETIRA, no solo se esconde (Codex final): si el lector volviera a irse, la terminal no puede
  // reaparecer elegida sin que nadie la eligiera. Desde aquí hay que decir cómo se cobra la tarjeta.
  const autoCardWithdrawn = cardAutoStale();
  useEffect(() => {
    if (!autoCardWithdrawn) return;
    setPaymentMethod('CARD_UNSET');
    setCardAutoSaleId(undefined);
  }, [autoCardWithdrawn]);
  /** Lo escrito en «Recibí» para esta venta; si no se tocó, vacío (P-081-C1: es opcional, solo para el cambio). */
  const cashTextFor = (sale: NativeSale): string => (cashText?.saleId === sale.id ? cashText.text : '');
  const cashFor = (sale: NativeSale): PosCashReceived => describeCashReceived(sale.totalAmount, cashTextFor(sale));

  const preview = useMemo(() => calculateCartPreview(cart), [cart]);
  // Deuda (r): qué puede afirmar el banner de ventas recuperables. Se deriva de los cobros que la
  // lista ya trae; no hace ninguna consulta nueva.
  // P-081: la venta LIMPIA que salió del pedido en pantalla (se volvió del cobro con Atrás) no se lista como «otra»
  // venta guardada mientras el pedido no cambie: «Cobrar» la retoma. Si el pedido cambia, deja de ser suya y aparece.
  // Una que pasó por el lector se lista siempre.
  const orderSaleId = cartSaleIdRef.current;
  const listedRecoverable = recoverable.filter((sale) => !(sale.id === orderSaleId && classifySaleCardTruth(sale) === 'clean'));
  const recovery = describeRecoverableCardRisk(listedRecoverable);

  /** Ventas a medias de esta tienda: se consultan al abrir el POS y al cambiar de tienda. */
  const loadRecoverable = useCallback(async (activeStoreId: string) => {
    if (!activeStoreId) { setRecoverable([]); return; }
    try {
      const result = await listPendingNativeSales(activeStoreId);
      setRecoverable(result.items);
    } catch {
      // Que falle la consulta de pendientes no debe impedir vender: se omite el aviso, nada más.
      setRecoverable([]);
    }
  }, []);

  useEffect(() => { void loadRecoverable(storeId); }, [storeId, loadRecoverable]);

  /** Paso 1 — "Cobrar": guarda la venta en el servidor ANTES de cobrar nada. */
  const startCheckout = async () => {
    // Prevención de doble envío. P-080 (Codex R1): `isSaving` es estado y llega en el render siguiente;
    // con «Cobrar» en la barra y en la hoja, dos toques seguidos podían entrar los dos. El candado del
    // `ref` se toma antes de la primera espera y se suelta al terminar. La clave de intención sigue
    // siendo la misma: aun así, el servidor devolvería la misma venta.
    if (isSaving || openingRef.current) return;
    // Revisión independiente de P-067: mientras «Continuar» pregunta por una venta guardada, abrir otra
    // aquí dejaba que esa respuesta tardía la reemplazara y soltara sus claves (y al volver a cobrar,
    // nacía otra venta para el mismo pedido). Se espera a que termine.
    if (resumingSaleId !== undefined) return;
    if (!storeId) { setCheckoutError({ message: 'Selecciona una tienda para vender.', ambiguous: false }); return; }
    if (isCartEmpty(cart)) { setCheckoutError({ message: 'Agrega al menos un producto al pedido.', ambiguous: false }); return; }
    openingRef.current = true;
    setIsSaving(true);
    setCheckoutError(undefined);
    setCanceledNotice(undefined);
    // Clave por intención de cobro: un doble toque de "Cobrar" devuelve la MISMA venta guardada.
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = randomUuid();
    try {
      const result = await openNativeSale({ storeId, idempotencyKey: idempotencyKeyRef.current, lines: buildCheckoutLines(cart) });
      // `openNativeSale` es IDEMPOTENTE: con la misma clave devuelve una venta YA EXISTENTE, con sus
      // cobros. Afirmar 'clean' aquí era el camino corto al agujero que este lote dice cerrar: tras
      // «Dejar pendiente y seguir vendiendo» el carrito y la clave siguen vivos, así que «Cobrar»
      // volvía a entrar a la venta en duda y la pantalla la declaraba limpia — candado fuera, métodos
      // de vuelta y efectivo a un toque. Se CLASIFICA lo que el servidor devolvió; ya trae los cobros,
      // así que no cuesta ninguna consulta.
      const truth = classifySaleCardTruth(result.sale);
      if (truth === 'confirmed') { finishSale(result.sale); return; }
      if (truth === 'closed') {
        void loadRecoverable(storeId);
        setCheckoutError({
          ambiguous: false,
          message: result.sale.status === 'CANCELED'
            ? 'Esta venta ya estaba cancelada. Empieza una venta nueva.'
            : 'Esta venta se anuló después de cobrarse. Revísala en el Historial.',
        });
        return;
      }
      if (truth === 'paid-not-completed') {
        // FI-53: la venta que salió de este pedido YA tiene el cobro confirmado y sigue sin
        // completar. No se abre el cobro con Efectivo elegido: se completa.
        cartSaleIdRef.current = result.sale.id;
        await settlePaidSale(result.sale.id, result.sale);
        return;
      }
      paymentKeyRef.current = randomUuid(); // clave nueva para el cobro de esta venta
      // Deuda (k): la venta ya existe en el servidor y salió de este carrito. Solo ahora, nunca antes.
      setMustPickMethod(false);
      cartSaleIdRef.current = result.sale.id;
      setCardTruth({ saleId: result.sale.id, truth });
      setPendingSale(result.sale);
      setPaymentMethod('CASH');
    } catch (saveError) {
      // Se conserva el carrito; nunca hay reintento automático. Un error ambiguo (red/timeout) no
      // afirma que la venta se guardó ni que falló: se ofrece reintentar con la MISMA clave.
      setCheckoutError(classifyCheckoutError(saveError));
    } finally {
      openingRef.current = false;
      setIsSaving(false);
    }
  };

  /**
   * ¿Esta venta tiene un cobro sin resolver? Se calcula aquí y no solo en el render, porque el
   * `disabled` de un botón NO es una guarda: «Reintentar de forma segura» del error ambiguo llama a
   * `confirmPayment` con su propio `disabled={isSaving}` y se saltaba el candado — y con el candado
   * puesto el bloque de métodos no se pinta, así que el método que iba a registrar era invisible.
   */
  const saleIsLocked = (): boolean => {
    const truth = cardTruth?.saleId === pendingSale?.id ? cardTruth?.truth : undefined;
    // SOLO 'in-doubt' —un intento realmente EN VILO— cierra la pantalla. 'card-unresolved' avisa pero
    // NO bloquea, y la diferencia importa mucho:
    //
    // Tras la lista blanca del lote 3, un cobro con tarjeta en PENDING corresponde casi siempre a algo
    // que el SERVIDOR sabe limpio: un rechazo determinante (BelowMinimum es muy plausible en ventas de
    // $10) o un lector que nunca llegó a abrirse. Bloquear ahí rompía la operación normal — el propio
    // mensaje del servidor dice «no se leyó ninguna tarjeta, cobra en efectivo» mientras la pantalla
    // escondía el efectivo, apagaba «Cancelar venta» y apagaba «Confirmar cobro»: la app ordenaba algo
    // que ella ya no podía hacer. Y el caso real de las 18:29 del 2026-09-29 —dos cobros caducados y el
    // tercero bueno— habría sido imposible de completar.
    //
    // Además coincide con el servidor, que tampoco bloquea un cobro PENDING: solo lo hace con un
    // intento en vilo. El riesgo que queda en 'card-unresolved' es la deuda (o), acotada y documentada.
    //
    // FI-53: 'paid-not-completed' también cierra la pantalla, y con más razón que 'in-doubt': ahí el
    // cobro no está en duda, está CONFIRMADO. No se cobra de otra forma ni se cancela.
    return truth === 'in-doubt' || truth === 'paid-not-completed' || !!zettlePendingAttemptId;
  };

  /** FI-53: ¿el servidor dijo que ESTA venta tiene el cobro confirmado y sigue sin completar? */
  const saleIsPaid = (): boolean => pendingSale !== undefined && cardTruth?.saleId === pendingSale.id && cardTruth.truth === 'paid-not-completed';

  /** Paso 2 — "Confirmar cobro": completa la venta y ejecuta sus efectos exactamente una vez. */
  const confirmPayment = async () => {
    if (isSaving || !pendingSale) return;
    // FI-53: con el cobro ya confirmado, este botón —y «Reintentar de forma segura», que también
    // llega aquí— NO cobra: ni abre el lector ni registra un cobro manual. Solo pide completar la
    // venta. Va antes que todo lo demás porque un `disabled` no es una guarda.
    if (saleIsPaid()) {
      await completePaidSale(pendingSale.id);
      return;
    }
    // Cobro con lector: flujo integrado (attempt del servidor → SDK → observación), nunca payNativeSale.
    if (paymentMethod === 'CARD_READER') {
      // PAYPAL-GATE-02: con un cobro con lector en duda en esta sesión, el único camino es verificarlo
      // por la MISMA referencia — ni preflight, ni avisos que lo tapen, ni un intento nuevo. (Llega aquí
      // por «Reintentar de forma segura» tras un error de red al verificar.)
      if (zettlePendingAttemptId) {
        await verifyZettleAttempt();
        return;
      }
      await runZettleCharge(pendingSale.id);
      return;
    }
    // Cobrar de OTRA forma una venta con un cobro con tarjeta sin resolver: aquí se para, venga el
    // toque de donde venga. El servidor lo rechaza mientras el intento esté en vilo, pero con el
    // cobro en PENDING lo ACEPTA, así que esta es la única barrera del cliente.
    if (saleIsLocked() || mustPickMethod) return;
    // P-081-C1: sin un método elegido por la persona no se registra ninguno (nada viene elegido de antemano).
    if (!methodPicked) return;
    // B0-03: «Tarjeta» sin camino elegido no es un método. La pantalla apaga el botón, pero un
    // `disabled` no es una guarda: aquí se para, y lo que sigue ya solo conoce los cuatro métodos
    // que el servidor acepta.
    if (paymentMethod === 'CARD_UNSET') return;
    // P-080: la terminal de fuera que la pantalla eligió sola dejó de ser el único camino (apareció el
    // lector): hay que decir cómo se cobra. Mismo motivo que la guarda anterior.
    if (cardAutoStale()) return;
    // P-080 (D-P080-CASH-RECEIVED-VISUAL): en efectivo, lo recibido tiene que cubrir el total —el cobro es
    // completo, no hay pagos parciales—. Aquí y no solo en el botón: «Reintentar de forma segura» también
    // llega. Va DESPUÉS de FI-53 (arriba): completar una venta ya cobrada no depende de «Recibí». Lo
    // recibido NO viaja: el cobro sigue siendo el método y la clave.
    if (paymentMethod === 'CASH' && !cashCovers(cashFor(pendingSale))) return;
    setIsSaving(true);
    setCheckoutError(undefined);
    if (!paymentKeyRef.current) paymentKeyRef.current = randomUuid();
    try {
      const result = await payNativeSale(pendingSale.id, { method: paymentMethod, idempotencyKey: paymentKeyRef.current });
      // `alreadyCompleted` = ya estaba cobrada (reintento o respuesta perdida): se muestra igual
      // como éxito, nunca se pide cobrar otra vez.
      finishSale(result.sale);
    } catch (payError) {
      if (payError instanceof ApiError && payError.payload?.error === 'NATIVE_SALE_ALREADY_CANCELED') {
        // Alguien la canceló mientras tanto: se sale del cobro para no dejar una pantalla imposible.
        setPendingSale(undefined);
        setCheckoutError({ message: 'Esta venta se canceló. Inicia una venta nueva.', ambiguous: false });
      } else if (saysPaymentConfirmed(payError) && (await adoptPaidSaleAfterRejection(pendingSale.id, payError, { tryCompletion: true }))) {
        // FI-53: el servidor negó el cobro manual porque la venta YA tiene un cobro confirmado (lo
        // confirmó mientras esta pantalla seguía ofreciendo cobrar). La pantalla ya dice eso.
      } else {
        setCheckoutError(classifyCheckoutError(payError));
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * PayPal/Zettle Entrega 1 — cobro con lector. La regla que gobierna el flujo: el servidor entrega
   * el intento (attempt.id = TransactionReference) y decide qué vale cada observación; un intento
   * REUTILIZADO jamás relanza el SDK (el lector pudo haber cobrado) — se recupera y se verifica por
   * la misma referencia.
   */
  const runZettleCharge = async (saleId: string) => {
    setIsSaving(true);
    setCheckoutError(undefined);
    setZettleNotice(undefined);
    // El intento cuyo resultado ESTA vuelta tiene sin entregar al servidor. `pendingObservationRef`
    // sobrevive a «Dejar pendiente y seguir vendiendo» (para poder reenviarlo al retomar la venta),
    // así que por sí solo no dice que el lector haya respondido AHORA: puede ser de otra venta.
    let unreportedAttemptId: string | undefined;
    try {
      // PAYPAL-GATE-02: en el build REAL, sesión del SDK y permisos del lector ANTES de pedir un
      // intento — así un "no" no deja un intento en vuelo. Developer Mode pasa directo, sin cambios.
      // Nada de esto autoriza el cobro: el servidor decide al entregar (o negar) el intento.
      // P-017: ¿hay un cobro con lector A MEDIAS que recuperar? Si el servidor lo tiene en vilo (cobro
      // PROCESSING) se pide el intento directo; si este equipo dejó una marca de lanzamiento, el preflight
      // corre pero no impide pedir el intento (solo impide abrir el lector). Un cobro PENDING tras un
      // intento cancelado ya no salta el preflight.
      const launched = readZettleLaunch(saleId);
      // PAYPAL-CURRENCY-01: el equipo dice en qué sesión del lector está y el SERVIDOR decide si
      // entrega el intento. La lectura es de solo lectura, no abre nada y nunca lanza; si no hay
      // nada que decir no se manda y el servidor no bloquea.
      const deviceSession = zettleSessionAttestation(await readZettleSessionIdentity());
      const begun = await startZettleAttemptAfterPreflight(
        zettleAvailability,
        { refreshAvailability: checkZettleAvailability, ensurePermissions: ensureZettleReaderPermissions },
        () => startPaymentAttempt(saleId, deviceSession),
        { skipPreflight: hasReaderPaymentInFlight(pendingSale?.payments), recoverEvenIfBlocked: launched !== undefined },
      );
      setZettleAvailability(begun.availability);
      if (begun.kind === 'blocked') {
        // Sin lector disponible, la opción desaparece: no se deja el método de pago apuntando a ella.
        if (!begun.availability.available) setPaymentMethod('CASH');
        setMustPickMethod(true);
        setZettleNotice(describeZettlePreflightBlock(begun.reason, { readerPaymentPending: await readerPaymentPendingOnServer(saleId) }));
        return;
      }
      const start = begun.start;
      // P-081 (Codex): desde que el servidor entregó un intento, esta venta pasó por el lector (aunque el lector no llegue
      // a abrirse): Atrás ya no la saca del cobro. Un bloqueo previo (sin sesión, sin permisos) no cuenta.
      setReaderTriedSaleId(saleId);
      const step = decideZettleStep(start);
      if (step === 'launch') {
        if (begun.blocked) {
          // La marca era vieja: el servidor entregó un intento NUEVO y el preflight no deja abrir el lector.
          // No se abre nada; ese intento se cierra en el acto y se muestra el bloqueo. Si el reporte no
          // llega, «Verificar cobro» lo reintenta (mismo cuerpo, idempotente).
          forgetZettleLaunch(saleId);
          pendingObservationRef.current = { attemptId: start.attemptId, body: NOT_LAUNCHED_OBSERVATION };
          unreportedAttemptId = start.attemptId;
          await reportPaymentObservation(start.attemptId, NOT_LAUNCHED_OBSERVATION);
          pendingObservationRef.current = undefined;
          if (!begun.availability.available) setPaymentMethod('CASH');
          setMustPickMethod(true);
          setZettleNotice(describeZettlePreflightBlock(begun.blocked, { readerPaymentPending: await readerPaymentPendingOnServer(saleId) }));
          return;
        }
        // P-017: la marca vive desde ANTES de abrir el lector hasta que el servidor recibe el resultado.
        rememberZettleLaunch(saleId, start.attemptId);
        const payload = await chargeWithZettle({ attemptId: start.attemptId, amountMinor: start.amountMinor });
        // Lo que dijo el SDK se conserva hasta entregarlo al servidor: puede ser un cobro realizado.
        const body = observationFromBridge(payload, 'PAYMENT_RESULT', { fallbackCurrency: start.currency });
        pendingObservationRef.current = { attemptId: start.attemptId, body };
        unreportedAttemptId = start.attemptId;
        // Deuda (t): desde aquí y hasta que el servidor responda, la pantalla lo DICE.
        setConfirmingWithServer(true);
        const response = await reportPaymentObservation(start.attemptId, body);
        pendingObservationRef.current = undefined;
        await applyZettleResponse(start.attemptId, response);
      } else if (step === 'recover') {
        // La app volvió a abrir con un cobro en vuelo: se reporta honestamente "no sé" y se verifica.
        const response = await reportPaymentObservation(start.attemptId, RECOVERY_OBSERVATION);
        await applyZettleResponse(start.attemptId, response);
      } else if (step === 'reconcile') {
        setZettlePendingAttemptId(start.attemptId);
        setZettleNotice(describeZettleOutcome({ attemptStatus: 'UNKNOWN' }));
      } else {
        // El intento ya tiene desenlace: solo refrescar la venta.
        const { sale } = await getNativeSale(saleId);
        if (sale.status === 'CONFIRMED') finishSale(sale);
        // FI-53: un intento ya confirmado con la venta sin completar no vuelve a la pantalla de cobro.
        else if (classifySaleCardTruth(sale) === 'paid-not-completed') await settlePaidSale(sale.id, sale);
        else setPendingSale(sale);
      }
    } catch (chargeError) {
      // ¿El lector respondió en ESTA vuelta y lo que falló fue entregárselo al servidor?
      const sdkAnswered = unreportedAttemptId !== undefined && pendingObservationRef.current?.attemptId === unreportedAttemptId;
      // FI-53: si el servidor CONTESTÓ con un rechazo después de que el lector respondió, el cobro
      // pudo quedar confirmado y la venta sin completar (es justo lo que rechaza: «no hay
      // existencias»). Eso no es «se perdió la conexión»: se le pregunta cómo quedó la venta. Lo
      // mismo si niega abrir un intento porque la venta ya tiene un cobro confirmado.
      if (sdkAnswered && serverAnswered(chargeError) && (await adoptPaidSaleAfterRejection(saleId, chargeError))) return;
      if (saysPaymentConfirmed(chargeError) && (await adoptPaidSaleAfterRejection(saleId, chargeError, { tryCompletion: true }))) return;
      if (sdkAnswered && pendingObservationRef.current) {
        // El SDK ya respondió pero el servidor no se enteró: NO se descarta. Verificar reintenta
        // el MISMO reporte (idempotente) en cuanto haya señal.
        setZettlePendingAttemptId(pendingObservationRef.current.attemptId);
        setZettleNotice({
          tone: 'warning',
          title: 'No sabemos si el cobro quedó registrado',
          detail: 'Se perdió la conexión con el servidor. No vuelvas a cobrar: usa "Verificar cobro" en cuanto vuelva la señal.',
        });
      } else {
        // PAYPAL-CURRENCY-01: el servidor NO entregó intento, así que el lector no se abrió y no hay
        // dinero en juego. Va al área de avisos del lector, no al error genérico. Y se obliga a
        // elegir la forma de pago A MANO: preseleccionar efectivo fue la causa de la venta fantasma
        // del 2026-09-29, así que aquí no se preselecciona nada.
        const deviceBlock = deviceSessionBlockMessage(chargeError);
        if (deviceBlock) {
          setMustPickMethod(true);
          setZettleNotice(describeZettleDeviceSessionBlock(deviceBlock));
          return;
        }
        // P-017: la venta ya no se puede cobrar (cancelada o cerrada por otro camino): la marca sobra.
        if (chargeError instanceof ApiError && /^NATIVE_SALE_(NOT_PAYABLE|ALREADY_CANCELED|NOT_FOUND)$/.test(String(chargeError.payload?.error))) forgetZettleLaunch(saleId);
        setCheckoutError(classifyCheckoutError(chargeError));
      }
    } finally {
      setConfirmingWithServer(false);
      setIsSaving(false);
    }
  };

  /**
   * P-017 (revisión independiente): el aviso de un preflight bloqueado tiene que hablar de lo que el
   * SERVIDOR tiene, no de lo que traía el último render: la fila del cobro con tarjeta la crea el servidor
   * al entregar el intento, así que `pendingSale` puede no haberla visto todavía. Se pregunta con la ruta
   * de SOLO LECTURA `GET /api/sales/:id`; si falla, se prefiere el aviso genérico (que ya advierte no
   * cobrar de otra forma). Nunca pide un intento ni toca el SDK.
   */
  const readSaleFromServer = async (
    saleId: string,
  ): Promise<{ ok: true; sale: NativeSale; truth: SaleCardTruth } | { ok: false }> => {
    // Deuda (q): se eliminó el atajo que devolvía `true` mirando `pendingSale` del render anterior.
    // Si `pendingSale` era OTRA venta (o quedaba de la anterior), el veredicto salía de la venta
    // equivocada. Ahora hay UNA sola pregunta al servidor y todos la hacen igual.
    try {
      const { sale } = await getNativeSale(saleId);
      const truth = classifySaleCardTruth(sale);
      // Antes, una venta que volvía ya CONFIRMED se descartaba en silencio; ahora se devuelve y
      // quien llama decide.
      if (sale.status === 'AWAITING_PAYMENT' && pendingSale?.id === saleId) setPendingSale(sale);
      // El veredicto se GUARDA, no solo se devuelve: si no, el candado del flujo vivo del cobro
      // —donde está el dinero— seguiría colgado solo de la marca local, que es justo lo que este
      // lote dice arreglar. La marca no existe en otro equipo ni tras borrar los datos de la app.
      setCardTruth({ saleId, truth });
      return { ok: true, sale, truth };
    } catch {
      // No debe lanzar NUNCA: sus llamadores viven dentro del try/catch grande del cobro.
      return { ok: false };
    }
  };

  /**
   * Lote 5: cierra la venta con la declaración del dueño. El servidor impone todo lo que importa
   * (solo dueño, nunca encima de un cobro confirmado, fundamento obligatorio); aquí solo se envía y
   * se refresca la lista. Es idempotente: si la respuesta se perdió y se reintenta, el servidor
   * responde que ya estaba cerrada en vez de un error.
   */
  const confirmDeclaration = async () => {
    if (!declaring || isDeclaring) return;
    setIsDeclaring(true);
    setDeclarationError(undefined);
    try {
      const result = await declareNativeSaleUnpaid(declaring.id, declarationBasis.trim());
      setDeclaring(undefined);
      setDeclarationBasis('');
      setCanceledNotice({
        title: result.alreadyDeclared ? 'Esta venta ya estaba cerrada' : 'Venta cerrada con tu declaración',
        detail: result.message,
        totalAmount: declaring.totalAmount,
      });
      await loadRecoverable(storeId);
    } catch (declareError) {
      // El mensaje viene del SERVIDOR: es quien sabe por qué se negó (un cobro que se confirmó
      // mientras declarabas, por ejemplo), y ese texto es más útil que cualquiera genérico.
      setDeclarationError(getErrorMessage(declareError, 'No fue posible cerrar la venta.'));
    } finally {
      setIsDeclaring(false);
    }
  };

  /** ¿El servidor dice que esta venta tiene un cobro con tarjeta sin resolver? */
  const readerPaymentPendingOnServer = async (saleId: string): Promise<boolean> => {
    const seen = await readSaleFromServer(saleId);
    return seen.ok && (seen.truth === 'in-doubt' || seen.truth === 'card-unresolved');
  };

  // ─── FI-53 · «cobro confirmado, venta sin completar» ────────────────────────────────────────────
  // El servidor confirma el cobro y completa la venta en dos pasos. Si el segundo no termina
  // (faltaron existencias, un producto se desactivó, el servidor se cayó en medio) queda una venta
  // ya COBRADA que sigue por completar. Antes la pantalla no conocía ese estado: daba el intento
  // confirmado por una venta completada y cerraba el ciclo, o la reabría como si nunca se hubiera
  // cobrado, con Efectivo elegido. Ahora es un estado propio: candado (ni se cobra de otra forma ni
  // se cancela), el aviso dice solo lo que se sabe, y la única acción es «Completar la venta», que
  // en el servidor es idempotente y no cobra.

  /** Deja la pantalla diciendo que esta venta tiene el cobro confirmado. No habla con el servidor. */
  const holdPaidSale = (saleId: string, sale: NativeSale | undefined, notice: ZettleNotice) => {
    // El servidor ya tiene el resultado del lector: la marca local ya no hace falta.
    forgetZettleLaunch(saleId);
    if (sale) setPendingSale(sale);
    setCardTruth(sale ? { saleId, truth: 'paid-not-completed' } : { saleId, truth: 'paid-not-completed', unread: true });
    // El cobro ya no está en duda: no hay nada que verificar ni que vigilar por esa referencia.
    setZettlePendingAttemptId(undefined);
    setMustPickMethod(false);
    setZettleNotice(notice);
  };

  /**
   * Pide al servidor completar la venta ya cobrada. Solo se cierra el ciclo si la devuelve CONFIRMED.
   * Quien llama maneja `isSaving`.
   */
  const requestPaidCompletion = async (saleId: string) => {
    try {
      const result = await completePaidNativeSale(saleId);
      if (result.sale.status === 'CONFIRMED') {
        finishSale(result.sale);
        return;
      }
      // El servidor contestó y la venta sigue sin completar: se dice lo que él dijo. Y como la devolvió,
      // ya se sabe cómo está (por si antes no se había podido leer).
      if (result.sale.status === 'AWAITING_PAYMENT') holdPaidSale(saleId, result.sale, READER_SALE_PAID_NOTICE);
      setCheckoutError({ ambiguous: false, message: result.message || 'El servidor todavía no pudo completar esta venta.' });
    } catch (completionError) {
      // Si entretanto la venta ya se cerró (otro camino la completó), este error ya no dice nada.
      if (finishedSaleIdRef.current === saleId) return;
      setCheckoutError(describePaidCompletionError(completionError));
    }
  };

  /**
   * Entra al estado y, como completar es idempotente y no cobra, lo intenta UNA vez sin esperar a que
   * la persona toque nada: si el servidor ya puede, la venta se completa sola. Si no, quedan el
   * motivo, «Completar la venta» y «Dejar pendiente y seguir vendiendo». `sale` es la venta tal como
   * la devolvió el servidor; sin ella (no se pudo leer) solo se afirma el cobro.
   */
  const settlePaidSale = async (saleId: string, sale: NativeSale | undefined) => {
    holdPaidSale(saleId, sale, sale ? READER_SALE_PAID_NOTICE : READER_SALE_PAID_UNREAD_NOTICE);
    await requestPaidCompletion(saleId);
  };

  /** Lo mismo, desde un camino que no tiene tomada la pantalla («Continuar», el vigilante). */
  const settlePaidSaleFromIdle = async (saleId: string, sale: NativeSale) => {
    if (isSavingRef.current) return;
    setIsSaving(true);
    setCheckoutError(undefined);
    try {
      await settlePaidSale(saleId, sale);
    } finally {
      setIsSaving(false);
    }
  };

  /** «Completar la venta», y «Reintentar de forma segura» en ese estado: el toque de la persona. */
  const completePaidSale = async (saleId: string) => {
    if (isSaving) return;
    setIsSaving(true);
    setCheckoutError(undefined);
    try {
      await requestPaidCompletion(saleId);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * El servidor RECHAZÓ algo sobre esta venta: antes de decir «no sabemos» o un error genérico, se le
   * pregunta cómo quedó. Devuelve `true` si con eso la pantalla ya dice la verdad —la venta estaba
   * completada, o tiene el cobro confirmado y falta completarla—; `false` si hay que seguir como
   * siempre. No lanza nunca.
   *
   * `tryCompletion`: el rechazo NO fue de un intento de completar (fue de cobrar, cancelar o abrir
   * otro intento), así que completar todavía no se ha intentado y se intenta. Sin él, el rechazo
   * recibido ES el motivo por el que el servidor no pudo completar, y se muestra tal cual.
   */
  const adoptPaidSaleAfterRejection = async (saleId: string, rejection: unknown, options: { tryCompletion?: boolean } = {}): Promise<boolean> => {
    const seen = await readSaleFromServer(saleId);
    if (!seen.ok) {
      // Revisión independiente de P-067 (HIGH): si el rechazo ES «esta venta ya tiene un cobro
      // confirmado», eso ya es la verdad que importa, aunque la venta no se pueda leer: la pantalla no
      // puede seguir ofreciendo cobrar. Se pone el candado sin afirmar nada de la venta. Solo con la
      // venta que está en el cobro: una de la lista no se abre por un error.
      if (!saysPaymentConfirmed(rejection) || pendingSale?.id !== saleId) return false;
      if (options.tryCompletion) {
        await settlePaidSale(saleId, undefined);
        return true;
      }
      holdPaidSale(saleId, undefined, READER_SALE_PAID_UNREAD_NOTICE);
      setCheckoutError(describePaidCompletionError(rejection));
      return true;
    }
    if (seen.truth === 'confirmed') {
      finishSale(seen.sale);
      return true;
    }
    if (seen.truth !== 'paid-not-completed') return false;
    if (options.tryCompletion) {
      await settlePaidSale(saleId, seen.sale);
      return true;
    }
    holdPaidSale(saleId, seen.sale, READER_SALE_PAID_NOTICE);
    setCheckoutError(describePaidCompletionError(rejection));
    return true;
  };

  /** "Verificar cobro": reintenta el reporte pendiente o consulta por la MISMA referencia (retrieve). */
  const verifyZettleAttempt = async () => {
    if (isSaving || !zettlePendingAttemptId) return;
    const verifiedSaleId = pendingSale?.id;
    setIsSaving(true);
    setCheckoutError(undefined);
    try {
      const attemptId = zettlePendingAttemptId;
      let response: PaymentObservationResponse;
      if (pendingObservationRef.current?.attemptId === attemptId) {
        response = await reportPaymentObservation(attemptId, pendingObservationRef.current.body);
        pendingObservationRef.current = undefined;
      } else {
        const payload = await retrieveWithZettle({ attemptId });
        response = await reportPaymentObservation(attemptId, observationFromBridge(payload, 'RETRIEVE'));
      }
      await applyZettleResponse(attemptId, response);
    } catch (verifyError) {
      // P-015: si entretanto el servidor ya confirmó y la venta se cerró, este error ya no dice nada.
      if (verifiedSaleId && finishedSaleIdRef.current === verifiedSaleId) return;
      // FI-53: el servidor contestó con un rechazo. Puede ser que el cobro ya esté confirmado y lo
      // que no pudo fue completar la venta: se le pregunta antes de mostrar el error a secas.
      if (verifiedSaleId && serverAnswered(verifyError) && (await adoptPaidSaleAfterRejection(verifiedSaleId, verifyError))) return;
      setCheckoutError(classifyCheckoutError(verifyError));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * PAYPAL-GATE-02 (solo build REAL): abre el inicio de sesión OFICIAL del Payments SDK. La sesión la
   * guarda el SDK; la web no ve credenciales ni tokens. El siguiente «Confirmar cobro» vuelve a
   * consultar si ya hay sesión.
   */
  const openZettleLogin = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const { started } = await startZettleLogin();
      setZettleNotice(ZETTLE_LOGIN_OPENED_NOTICE);
      if (!started) {
        // P-015: ya había sesión y no se abrió otro login: se consulta y el aviso se corrige solo.
        const availability = await checkZettleAvailability();
        setZettleAvailability(availability);
        setZettleNotice((current) => noticeAfterZettleSessionCheck(current, availability));
      }
    } catch {
      setZettleNotice({ tone: 'error', title: 'No se pudo abrir el inicio de sesión de Zettle', detail: 'Intenta de nuevo. No se inició ningún cobro nuevo.' });
    } finally {
      setIsSaving(false);
    }
  };

  /** P-015: permiso del lector que Android ya no pregunta → Ajustes de la app. No inicia ningún cobro. */
  const openAppSettings = async () => {
    try {
      await openZettleAppSettings();
    } catch {
      setZettleNotice({ tone: 'error', title: 'No se pudieron abrir los ajustes', detail: 'Ábrelos a mano: Ajustes › Aplicaciones › esta app › Permisos. No se inició ningún cobro nuevo.' });
    }
  };

  /** Aplica lo que el SERVIDOR confirmó (nunca lo que dijo el SDK a solas). */
  const applyZettleResponse = async (attemptId: string, response: PaymentObservationResponse) => {
    // P-017: el servidor ya tiene el resultado de este lanzamiento; la marca local ya no hace falta.
    forgetZettleLaunch(response.saleId);
    if (response.attemptStatus === 'SUCCEEDED') {
      if (finishedSaleIdRef.current === response.saleId) return; // P-015: ya la cerró el vigilante
      setZettlePendingAttemptId(undefined);
      setZettleNotice(undefined);
      // FI-53: «intento SUCCEEDED» es el COBRO, no la venta. El servidor confirma el cobro y completa
      // la venta en dos pasos, y el segundo puede no haber terminado. Antes se cerraba el ciclo con
      // la venta que se leyera, estuviera como estuviera —y, si la lectura fallaba, a ciegas, dando
      // por hecho que «quedó completada»—. Ahora el ciclo solo se cierra con una venta que el
      // servidor devuelve CONFIRMED.
      const seen = await readSaleFromServer(response.saleId);
      if (finishedSaleIdRef.current === response.saleId) return; // el vigilante la cerró mientras se leía
      if (seen.ok && seen.truth === 'confirmed') {
        finishSale(seen.sale);
        return;
      }
      if (seen.ok && seen.truth === 'closed') {
        // Cobro confirmado sobre una venta cancelada o anulada: no hay nada que completar, y tampoco
        // se puede decir «no se registró ningún cobro».
        forgetZettleLaunch(response.saleId);
        setPendingSale(undefined);
        setCardTruth(undefined);
        paymentKeyRef.current = undefined;
        void loadRecoverable(storeId);
        setCheckoutError({ ambiguous: false, message: describePaidSaleClosed(seen.sale.status) });
        return;
      }
      // Cobro confirmado y venta sin completar —o sin poder leerla—: no se cierra nada.
      await settlePaidSale(response.saleId, seen.ok ? seen.sale : undefined);
      return;
    }
    // P-015: una respuesta atrasada de «Verificar cobro» no reabre una venta que ya se cerró.
    if (finishedSaleIdRef.current === response.saleId) return;
    const inDoubt = response.attemptStatus === 'UNKNOWN';
    setZettlePendingAttemptId(inDoubt ? attemptId : undefined);
    // Con el intento en duda el vigilante arranca (watchedSaleId cuelga de zettlePendingAttemptId),
    // asi que el estado real es «confirmando», no «no sabemos». La alarma la pone onExhausted.
    setZettleNotice(describeZettleOutcome(response, { watching: inDoubt }));
  };

  /** Cancelar antes de cobrar: no hubo efectos, así que no hay nada que revertir. */
  const cancelPending = async (saleId: string) => {
    if (isSaving) return;
    setIsSaving(true);
    setIsCanceling(true);
    setCheckoutError(undefined);
    try {
      const { sale } = await cancelNativeSale(saleId);
      forgetZettleLaunch(saleId);
      // Deuda (k): el carrito solo se vacía si esta venta salió de él. Cancelar una venta recuperada
      // no puede borrar lo que la persona ya tenía escaneado para otro cliente.
      // P-081 (Codex): si el pedido en pantalla es de OTRA venta guardada (se volvió del cobro con Atrás), su clave se
      // conserva: cancelar esta no puede hacer que «Cobrar» cree una segunda venta para aquel pedido.
      const cartOfAnotherSale = cartSaleIdRef.current !== undefined && cartSaleIdRef.current !== saleId;
      const cartCleared = shouldClearCartOnCancel(saleId, cartSaleIdRef.current);
      if (cartCleared) {
        setCart([]);
        cartSaleIdRef.current = undefined;
      }
      setPendingSale(undefined);
      setZettleNotice(undefined);
      setZettlePendingAttemptId(undefined);
      paymentKeyRef.current = undefined;
      if (!cartOfAnotherSale) idempotencyKeyRef.current = undefined;
      // Deuda (k): el importe sale de la RESPUESTA del servidor, no del último render.
      const cartState = cartCleared ? 'cleared' : isCartEmpty(cart) ? 'empty' : 'kept';
      setCanceledNotice({ ...describeCanceledSale({ cart: cartState }), totalAmount: sale.totalAmount });
      await loadRecoverable(storeId);
    } catch (cancelError) {
      // Una cancelación que falló no se anuncia como hecha: el aviso de cancelación no se pone.
      //
      // Y NO se reutiliza el error ambiguo del cobro: su botón «Reintentar de forma segura» está
      // cableado a `confirmPayment()`, es decir COBRA. Un parpadeo de red al cancelar más un toque
      // en ese botón terminaba en un cobro en efectivo registrado sin cobrar, o en un cargo real a
      // la tarjeta del cliente al que se le acababa de decir que la venta se cancelaba.
      //
      // El texto no afirma que la cancelación falló (no lo sabemos) y `cancelSale` es idempotente en
      // el servidor, así que «vuelve a tocar Cancelar venta» es consejo seguro.
      //
      // FI-53: el servidor no cancela una venta que YA tiene un cobro confirmado. Si es la que está en
      // el cobro, la pantalla pasa a decirlo; si se canceló desde la lista de guardadas, la lista se
      // vuelve a leer para que esa fila lleve su marca (y el motivo del servidor se muestra abajo).
      if (saysPaymentConfirmed(cancelError)) {
        if (pendingSale?.id === saleId && (await adoptPaidSaleAfterRejection(saleId, cancelError, { tryCompletion: true }))) return;
        void loadRecoverable(storeId);
      }
      const classified = classifyCheckoutError(cancelError);
      setCheckoutError({
        ambiguous: false,
        message: classified.ambiguous
          // El texto ambiguo del cobro habla de «la venta ya se haya registrado»: aquí engaña.
          ? 'No pudimos confirmar si la venta se canceló. Vuelve a tocar «Cancelar venta».'
          : classified.message,
      });
    } finally {
      setIsCanceling(false);
      setIsSaving(false);
    }
  };

  /**
   * Deuda (q) + decisión del dueño: la salida de la persona que atiende cuando una venta tiene un
   * cobro sin resolver. No cobra, no cancela, no resuelve nada: SOLO sale de esa pantalla para poder
   * seguir atendiendo. La venta sigue guardada en el servidor, intacta, y reaparece en el banner de
   * pendientes. No toca el servidor.
   */
  const leaveSalePending = () => {
    setPendingSale(undefined);
    setPickedSaleId(undefined); // P-081-C1: si se vuelve a abrir esta venta, se elige otra vez cómo te pagan.
    setZettleNotice(undefined);
    setZettlePendingAttemptId(undefined);
    setCardTruth(undefined);
    setCheckoutError(undefined);
    paymentKeyRef.current = undefined;
    void loadRecoverable(storeId);
  };

  /**
   * P-081 · Atrás desde «¿Cómo te pagan?»: de vuelta al catálogo con el pedido intacto. NO cancela: la venta queda
   * guardada sin cobrar (como «Dejar pendiente») y, mientras el pedido no cambie, «Cobrar» la retoma (la clave de
   * intención y la venta del pedido se conservan; el servidor devuelve la misma). Solo se ofrece con una venta
   * LIMPIA —que no pasó por el lector y sin reelección pendiente—: ver `canReturnToOrder`.
   */
  const returnToOrder = () => {
    setMustPickMethod(false);
    setPayLevel('methods');
    leaveSalePending();
  };

  /** ¿Atrás puede sacar del cobro esta venta? Solo si está limpia: nunca pasó por el lector ni hay que volver a elegir. */
  const canReturnToOrder = (): boolean => pendingSale !== undefined && !mustPickMethod && !zettlePendingAttemptId && readerTriedSaleId !== pendingSale.id
    && cardTruth?.saleId === pendingSale.id && cardTruth.truth === 'clean';

  /** Cierra el ciclo: limpia carrito y claves, y deja el POS listo para la siguiente venta. */
  const finishSale = (sale: NativeSale, options: { keepCart?: boolean } = {}) => {
    if (finishedSaleIdRef.current === sale.id) return; // P-015: una venta se cierra una sola vez
    finishedSaleIdRef.current = sale.id;
    forgetZettleLaunch(sale.id);
    setLastSale(sale);
    setCanceledNotice(undefined);
    setPendingSale(undefined);
    // Deuda (k): solo se vacia el carrito que produjo ESTA venta; una recuperada puede no tener nada
    // que ver con lo que la persona ya tiene escaneado para otro cliente.
    if (!options.keepCart && shouldClearCartOnCancel(sale.id, cartSaleIdRef.current)) setCart([]);
    setZettleNotice(undefined);
    setZettlePendingAttemptId(undefined);
    // P-081 (Codex): completar OTRA venta (una retomada) no le quita la clave al pedido en pantalla si es de otra venta
    // guardada: con el mismo pedido, «Cobrar» tiene que volver a SU venta, no crear una segunda.
    if (!(cartSaleIdRef.current !== undefined && cartSaleIdRef.current !== sale.id)) idempotencyKeyRef.current = undefined;
    paymentKeyRef.current = undefined;
    void loadRecoverable(storeId);
    onSaleCreated();
  };

  /**
   * Retomar una venta que quedó a medias — PREGUNTANDO AL SERVIDOR ANTES DE PINTAR.
   *
   * Deuda (q): esto era sincrónico y pintaba «Confirmar cobro» + «Cancelar venta» con «Efectivo»
   * preseleccionado, a partir de una fila de lista que podía tener minutos, habiendo BORRADO antes el
   * aviso y la referencia del intento. Es el camino por el que el 2026-09-29 se confirmó en efectivo
   * una venta por error, y el que dejaba al personal tocando botones en vano: el mensaje del servidor
   * los mandaba a «Verificar cobro», un botón que esta misma función acababa de quitar de la pantalla.
   */
  const resumeSale = async (sale: NativeSale) => {
    if (isSaving || resumingSaleId !== undefined) return;
    setLastSale(undefined);
    setCanceledNotice(undefined);
    setCheckoutError(undefined);
    setMustPickMethod(false);
    setResumingSaleId(sale.id);
    let seen: { ok: true; sale: NativeSale; truth: SaleCardTruth } | { ok: false };
    try {
      seen = await readSaleFromServer(sale.id);
    } finally {
      setResumingSaleId(undefined);
    }
    // Sin respuesta del servidor NO se abre la pantalla de cobro: sin saber cómo quedó, ofrecer
    // botones es justo el error que se está arreglando.
    if (!seen.ok) {
      setCheckoutError({
        ambiguous: false,
        message: 'No pudimos preguntarle al servidor cómo quedó esta venta. Sin conexión no sabemos si ya se cobró: no la cobres ni la canceles todavía. Vuelve a tocar «Continuar» cuando haya señal.',
      });
      return;
    }
    if (seen.truth === 'confirmed') {
      // Ya estaba cobrada: se cierra el ciclo con su folio y la fila desaparece del banner.
      finishSale(seen.sale, { keepCart: true });
      return;
    }
    if (seen.truth === 'closed') {
      void loadRecoverable(storeId);
      setCheckoutError({
        ambiguous: false,
        message: seen.sale.status === 'CANCELED'
          ? 'Esta venta ya estaba cancelada. Empieza una venta nueva si hace falta.'
          : 'Esta venta se anuló después de cobrarse. Revísala en el Historial.',
      });
      return;
    }
    if (seen.truth === 'paid-not-completed') {
      // FI-53: esta venta YA tiene el cobro confirmado y se quedó sin completar. No se abre como una
      // venta por cobrar —antes entraba con «Total a cobrar» y Efectivo elegido—: se completa.
      await settlePaidSaleFromIdle(seen.sale.id, seen.sale);
      return;
    }
    setPendingSale(seen.sale);
    setCardTruth({ saleId: seen.sale.id, truth: seen.truth });
    if (seen.truth === 'in-doubt' || seen.truth === 'card-unresolved') {
      // El candado se cuelga del VEREDICTO, no de la marca local: en otro equipo, o con los datos de
      // la app borrados, la marca no existe y antes eso dejaba la venta sin candado y sin salida.
      setZettleNotice(seen.truth === 'in-doubt' ? READER_SALE_IN_DOUBT_NOTICE : READER_SALE_CARD_UNRESOLVED_NOTICE);
      // 'card-unresolved' avisa pero deja cobrar y cancelar: el metodo se vuelve a elegir a proposito,
      // para que nadie confirme efectivo por inercia sobre una venta que ya paso por el lector.
      if (seen.truth === 'card-unresolved') setMustPickMethod(true);
      setZettlePendingAttemptId(readZettleLaunch(sale.id));
      return;
    }
    // 'clean': esta venta nunca tocó el lector, flujo normal.
    setZettleNotice(undefined);
    setZettlePendingAttemptId(undefined);
    paymentKeyRef.current = randomUuid();
    setPaymentMethod('CASH');
  };

  // P-015: con un cobro con lector EN DUDA, la pantalla activa vuelve a LEER la venta del servidor
  // (GET /api/sales/:id, solo lectura) hasta que este diga algo definitivo — la reconciliación corre sola
  // en el servidor y en P-014 la pantalla se quedó «pendiente» con la venta ya completada. El vigilante
  // jamás pide un intento, reporta una observación ni reconcilia: si el servidor confirmó, cierra el
  // ciclo igual que «Verificar cobro»; si no, la pantalla sigue bloqueada contra un segundo cobro.
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;
  const finishSaleRef = useRef(finishSale);
  finishSaleRef.current = finishSale;
  const settlePaidSaleRef = useRef(settlePaidSaleFromIdle);
  settlePaidSaleRef.current = settlePaidSaleFromIdle;
  const watchedSaleId = zettlePendingAttemptId ? pendingSale?.id : undefined;
  const watchedSaleIdRef = useRef(watchedSaleId);
  watchedSaleIdRef.current = watchedSaleId;
  useEffect(() => {
    if (!watchedSaleId) return undefined;
    const watcher = watchReaderSale<NativeSale>({
      fetchSale: async () => (await getNativeSale(watchedSaleId)).sale,
      onConfirmed: (sale) => finishSaleRef.current(sale),
      // Solo mientras ESTA venta siga en duda: si «Verificar cobro» ya trajo el desenlace, su aviso manda.
      onSettled: (sale) => {
        if (watchedSaleIdRef.current !== watchedSaleId) return;
        // FI-53: el servidor ya no tiene el cobro en vilo porque lo CONFIRMÓ, y la venta sigue sin
        // completar. No es «toca Verificar cobro para ver el resultado»: el resultado ya se sabe.
        if (classifySaleCardTruth(sale) === 'paid-not-completed') {
          void settlePaidSaleRef.current(sale.id, sale);
          return;
        }
        setZettleNotice(READER_SALE_SETTLED_NOTICE);
      },
      onExhausted: () => { if (watchedSaleIdRef.current === watchedSaleId) setZettleNotice(READER_SALE_STILL_IN_DOUBT_NOTICE); },
      isVisible: () => document.visibilityState !== 'hidden',
      isBusy: () => isSavingRef.current,
    });
    const onVisibility = () => { if (document.visibilityState !== 'hidden') watcher.checkNow(); };
    document.addEventListener('visibilitychange', onVisibility);
    const resumeListener = Capacitor.isNativePlatform() ? CapacitorApp.addListener('resume', () => watcher.checkNow()) : undefined;
    return () => {
      watcher.stop();
      document.removeEventListener('visibilitychange', onVisibility);
      void resumeListener?.then((listener) => listener.remove()).catch(() => undefined);
    };
  }, [watchedSaleId]);

  // ─── Rediseño · Bloque D: estado que solo sirve para DIBUJAR ────────────────────────────────────
  // Nada de esto decide dinero. Vive aquí, y no en los componentes que dibujan, porque las tres
  // pantallas (venta, cobro y venta completada) son este mismo componente: así el pedido, las claves
  // y un resultado del lector aún sin reportar no se pierden al pasar de una a otra.
  const [query, setQuery] = useState('');
  const [orderOpen, setOrderOpen] = useState(false);
  // P-080 (D-P080-CATALOG-VIEW): Lista o Tarjetas, por negocio y por persona. Lo guardado se lee con la
  // clave de quien está; lo elegido en esta pantalla solo vale para la clave con que se eligió, así que
  // otra cuenta en el mismo equipo ve SU vista y nunca se escribe la anterior bajo la clave nueva.
  const viewKey = posCatalogViewKey(business?.id ?? user?.businessId, user?.id);
  const storedView = useMemo(() => readPosCatalogView(browserStorage(), viewKey), [viewKey]);
  const [viewChoice, setViewChoice] = useState<{ key: string | undefined; view: PosCatalogView }>();
  const catalogView = viewChoice && viewChoice.key === viewKey ? viewChoice.view : storedView;
  const chooseCatalogView = (view: PosCatalogView) => { setViewChoice({ key: viewKey, view }); writePosCatalogView(browserStorage(), viewKey, view); };
  // P-080: «Cobrar» desde la barra deja el error de abrir la venta arriba del catálogo, que puede estar
  // desplazado hacia abajo. Un error nuevo se trae a la vista, como los avisos del cobro; y también cuando
  // pasa de la hoja al catálogo al cerrarla (Codex final: llegó con la hoja abierta y al cerrarla quedaba fuera).
  const saleErrorBox = useRef<HTMLDivElement>(null);
  // El error que todavía no se trajo a la vista sobre el catálogo (llegó con la hoja abierta, o no había dónde).
  const shownSaleError = useRef(checkoutError);
  const revealSaleError = useRef(false);
  useEffect(() => {
    if (shownSaleError.current !== checkoutError) revealSaleError.current = checkoutError !== undefined;
    shownSaleError.current = checkoutError;
    const box = saleErrorBox.current;
    const scroller = box ? scrollParentOf(box) : null;
    if (!revealSaleError.current || !box || !scroller) return;
    revealSaleError.current = false;
    const above = scroller.getBoundingClientRect().top + SALE_ERROR_MARGIN - box.getBoundingClientRect().top;
    if (above > 0) scroller.scrollTop -= above;
  }, [checkoutError, orderOpen]);
  const [confirmingClear, setConfirmingClear] = useState(false);
  /** La venta que se está por cancelar: cancelar pide confirmación antes de hablar con el servidor. */
  const [cancelTarget, setCancelTarget] = useState<NativeSale>();
  const [saleRoot, setSaleRoot] = useState<HTMLElement | null>(null);
  const split = useFitsSplit(saleRoot);
  // Bloque G: con el teclado de la pantalla abierto (lo mide el shell), la barra del pedido se esconde
  // (`pos.css`): pegada abajo tapaba el buscador y los resultados. Vuelve al cerrarse el teclado.
  const keyboardOpen = useSoftKeyboardFromShell();
  // El pedido, cuando va al lado del catálogo, no mide más que el hueco que le queda: así «Cobrar»,
  // que va a su pie, nunca queda por debajo del borde.
  const [orderPanel, setOrderPanel] = useState<HTMLElement | null>(null);
  const orderPanelRoom = useRoomBelow(orderPanel, saleRoot);

  // El Atrás del teléfono no se salta un cobro. Con una operación en curso o un cobro con tarjeta sin
  // resolver queda consumido —igual que con un diálogo ocupado— y la pantalla dice por qué. No
  // cobra, no cancela y no cierra nada: las salidas siguen siendo los botones.
  //
  // `posRoot` envuelve la pantalla Y sus diálogos: así esta guarda entra en la pila por debajo de
  // cualquier diálogo del punto de venta, y con uno abierto quien responde al Atrás, a Escape y al
  // Tab sigue siendo el diálogo (que, ocupado, tampoco se cierra).
  const posRoot = useRef<HTMLDivElement>(null);
  const [backBlocked, setBackBlocked] = useState(false);
  const backGuarded = isSaving || (pendingSale !== undefined && saleIsLocked());
  // P-081 · Atrás por niveles en el cobro (el del teléfono, el del navegador y «‹ Atrás»): del panel de un método a
  // «¿Cómo te pagan?»; de ahí al catálogo con el pedido intacto, sin cancelar. Con una operación en curso o un cobro
  // sin resolver, la guarda de siempre; con una venta que ya pasó por el lector, no sale y se dice por qué.
  // P-082 (decisión del dueño, tras su video): volver a «¿Cómo te pagan?» DESMARCA el método y cierra su panel —con
  // el panel abierto y el método marcado, el Atrás solo desplazaba y no se notaba—. Lo escrito en «Recibí» se queda
  // con la venta: al tocar Efectivo otra vez vuelve a estar.
  const stepBack = () => {
    if (backGuarded) { setBackBlocked(true); return; }
    if (!pendingSale) return;
    if (payLevel === 'detail') { setPayLevel('methods'); setPickedSaleId(undefined); setBackBlocked(false); return; }
    if (!canReturnToOrder()) { setBackBlocked(true); return; }
    returnToOrder();
  };
  useAndroidBackClose(stepBack, backGuarded || pendingSale !== undefined, posRoot);
  useEffect(() => { if (!backGuarded) setBackBlocked(false); }, [backGuarded]);
  // Una venta nueva en el cobro empieza en «¿Cómo te pagan?».
  useEffect(() => { setPayLevel('methods'); setBackBlocked(false); }, [pendingSale?.id]);
  // P-081 · en la web, el Atrás del navegador hace lo mismo que el del teléfono mientras haya un nivel al que
  // regresar: el cobro, la hoja del pedido o un diálogo del punto de venta (`utils/posBrowserBack.ts`).
  // Mientras se guarda o se cobra también: «Cobrar» desde la barra no abre la hoja, y sin guarda el Atrás saldría a
  // media operación (la guarda de siempre lo consume).
  const browserLevel = !Capacitor.isNativePlatform() && (isSaving || pendingSale !== undefined || orderOpen || confirmingClear
    || cancelTarget !== undefined || declaring !== undefined || pendingStoreId !== undefined);
  const browserLevelRef = useRef(browserLevel);
  browserLevelRef.current = browserLevel;
  const browserBack = useRef<{ sync: () => void; stop: () => void }>();
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    const watcher = watchPosBrowserBack(() => browserLevelRef.current, (hash) => hash.replace(/^#/, '') === POS_ROUTE);
    browserBack.current = watcher;
    return () => { watcher.stop(); browserBack.current = undefined; };
  }, []);
  useEffect(() => { browserBack.current?.sync(); }, [browserLevel]);

  // «Venta completada» afirma un éxito, así que solo se enciende con una venta que el servidor
  // devolvió COMPLETADA. `lastSale` no lo garantiza: el camino del lector cierra el ciclo con la venta
  // que lee después de un intento confirmado, sea cual sea su estado. Una venta en cualquier otro
  // estado se dice como lo que es, en un aviso sobre la pantalla de venta (ver más abajo).
  const completedSale = lastSale?.status === 'CONFIRMED' ? lastSale : undefined;
  // ONB-001: ¿es la primera venta completada del negocio? Solo se pregunta con `completedSale` (el
  // servidor ya la devolvió CONFIRMED), solo para el dueño, y lo responde el servidor.
  const isFirstSale = useFirstSale(completedSale?.id, business?.id, canSeeActivationGuide(user?.role));

  // Al pasar de una pantalla a otra, el foco y la vista van al principio de la nueva: quien usa un
  // lector de pantalla oye dónde quedó, y nadie aterriza a media página.
  const screen: PosScreen = pendingSale ? 'checkout' : completedSale ? 'done' : 'sale';
  const shownScreen = useRef(screen);
  useEffect(() => {
    if (shownScreen.current === screen) return;
    shownScreen.current = screen;
    // Lo que estuviera abierto era de la pantalla anterior.
    setOrderOpen(false);
    setConfirmingClear(false);
    setCancelTarget(undefined);
    const section = document.getElementById(SCREEN_TITLE[screen])?.closest<HTMLElement>('section');
    if (!section) return;
    const scroller = scrollParentOf(section);
    if (scroller) scroller.scrollTop = 0;
    section.focus({ preventScroll: true });
  }, [screen]);

  // Deuda (m): todos los hooks quedan ARRIBA de este punto; estos retornos tempranos no pueden
  // moverse por encima de ningún useEffect/useState.
  const storeState = posStoreState(storeId, stores, storesLoaded, storesFailed);
  if (storeState !== 'ready') {
    return (
      <section aria-label="Vender" className="n3-pos">
        <div className="n3-pos__stack">
          <PosViewLinks links={viewLinks} />
          {storeState === 'loading' && <LoadingPanel label="Cargando tiendas…" />}
          {storeState === 'error' && (
            <ErrorPanel
              description="Revisa la conexión y vuelve a entrar a Ventas e ingresos. No es que no tengas tiendas: no pudimos preguntarlo."
              title="No pudimos cargar tus tiendas"
            />
          )}
          {storeState === 'unassigned' && (
            <EmptyPanel
              description="No tienes ninguna tienda donde registrar ventas. Pide a un dueño o gerente que te asigne una."
              icon="stores"
              title="Sin tienda asignada"
            />
          )}
        </div>
      </section>
    );
  }

  const checkoutDisabled = isSaving || resumingSaleId !== undefined || isCartEmpty(cart);
  const zettleMethodHint = describeZettleMethodHint(zettleAvailability);
  // Deuda (q): el candado se cuelga del VEREDICTO del servidor atado a ESTA venta, no de la marca
  // local (que no existe en otro equipo ni tras borrar los datos) ni del render anterior.
  const readerLock = saleIsLocked();
  // FI-53: el candado es de un cobro ya CONFIRMADO, no de uno en duda. Mismo candado, otra verdad.
  const paidLock = saleIsPaid();
  // B0-03: el lector estaba elegido y este equipo dejó de tenerlo (lo dijo el puente al volver al
  // frente). No se queda elegido en silencio: se vuelve a preguntar cómo se cobra la tarjeta y el
  // botón espera. Solo es lo que se VE: lo elegido no cambia, y si el lector vuelve, vuelve elegido.
  // Mientras el lector cobra no aplica: ese cobro sigue su camino.
  const readerGone = paymentMethod === 'CARD_READER' && !zettleAvailability.available && !isSaving;
  // P-080: la terminal de fuera que se eligió sola ya no es el único camino. Se ve como «sin elegir».
  const autoCardStale = cardAutoStale();

  /** Cancelar pide confirmación; lo que cancela sigue siendo `cancelPending`, sin cambios. */
  const cancelDialog = cancelTarget && (
    <Dialog
      footer={<>
        <Button onClick={() => setCancelTarget(undefined)} variant="secondary">No, regresar</Button>
        <Button className="n3-pos__danger" onClick={() => { const saleId = cancelTarget.id; setCancelTarget(undefined); void cancelPending(saleId); }} variant="primary">Sí, cancelar</Button>
      </>}
      onClose={() => setCancelTarget(undefined)}
      title="¿Cancelar esta venta?"
    >
      <p className="n3-pos__dialog-text">La venta de {formatMoney(cancelTarget.totalAmount)} se cancela: no se registra ningún cobro y no se descuenta inventario.</p>
      {/* Una venta que pasó por el lector puede tener dinero cobrado: se dice antes de cancelar. */}
      {hasUnsettledCardPayment(cancelTarget.payments) && (
        <PosNotice role="status" tone="warning">
          Esta venta intentó cobrarse con el lector. Si no sabes cómo quedó ese cobro, ábrela con «Continuar» antes de cancelarla.
        </PosNotice>
      )}
    </Dialog>
  );

  // PANTALLA DE COBRO: una venta ya guardada, esperando que se registre cómo pagó el cliente. Ocupa
  // toda la vista a propósito — con el cliente enfrente hay UNA sola decisión que tomar.
  if (pendingSale) {
    // P-080: lo que «Recibí» dice frente al total del SERVIDOR. Solo cuenta con Efectivo elegido.
    const cash = cashFor(pendingSale);
    const cashShort = !readerLock && !mustPickMethod && methodPicked && paymentMethod === 'CASH' && !cashCovers(cash);
    return <div ref={posRoot}>
      <PosCheckout
        backBlocked={backBlocked}
        busy={isSaving}
        canVerify={readerLock && Boolean(zettlePendingAttemptId)}
        canceling={isCanceling}
        cardAuto={paymentMethod === 'CARD' && cardAutoSaleId === pendingSale.id && !autoCardStale}
        cash={{ text: cashTextFor(pendingSale), received: cash }}
        choice={readerGone || autoCardStale ? 'CARD_UNSET' : paymentMethod}
        // Con un cobro sin resolver, confirmar y cancelar se apagan: el backend los bloquearía igual,
        // pero el botón no debe invitar al error. Deuda (w): tras un intento con tarjeta que no llegó a
        // cobrar hay que volver a elegir el método. B0-03: «Tarjeta» sin camino elegido tampoco cobra.
        // P-080: ni efectivo que no cubre el total.
        cancelDisabled={isSaving || readerLock}
        // FI-53: con el cobro confirmado el botón principal es «Completar la venta», que no cobra:
        // solo se apaga mientras hay una operación en curso.
        confirmDisabled={paidLock ? isSaving : isSaving || readerLock || mustPickMethod || !methodPicked || paymentMethod === 'CARD_UNSET' || readerGone || autoCardStale || cashShort}
        confirming={confirmingWithServer}
        error={checkoutError}
        locked={readerLock}
        mustPick={mustPickMethod}
        // P-081-C1: el cobro abre sin método elegido; hasta que la persona toque uno, ninguno se ve elegido.
        unpicked={!methodPicked}
        notice={zettleNotice}
        paid={paidLock}
        payLevel={payLevel}
        paidUnread={paidLock && cardTruth?.unread === true}
        onCancel={() => setCancelTarget(pendingSale)}
        onBack={stepBack}
        onCardPath={pickCardPath}
        onCash={(text) => setCashText({ saleId: pendingSale.id, text })}
        onConfirm={() => void confirmPayment()}
        onLeavePending={() => leaveSalePending()}
        onLogin={() => void openZettleLogin()}
        onMethod={pickMethod}
        onOpenSettings={() => void openAppSettings()}
        onRetry={() => void confirmPayment()}
        onVerify={() => void verifyZettleAttempt()}
        // Cobro con lector: SOLO se ofrece dentro de la app Android con el puente disponible. La web
        // conserva su comportamiento de siempre (tarjeta = cobrada por fuera).
        readerHint={zettleMethodHint}
        readerOffered={zettleAvailability.available}
        revealTick={revealTick}
        sale={pendingSale}
        // Una venta retomada no salió del pedido que está en pantalla: sus productos van a la vista,
        // para reconocerla antes de cobrarla.
        showLines={cartSaleIdRef.current !== pendingSale.id}
        why={mustPickMethod && !readerLock
          ? 'El cobro con tarjeta no se realizó. Toca arriba cómo te pagaron para poder continuar.'
          // P-081-C1: sin método elegido no hay porqué en el pie: la pregunta «¿Cómo te pagan?» ya lo dice, y con la letra
          // al doble (320 × 568) ese renglón agrandaba tanto el pie pegado que tapaba los métodos (harness §9).
          : !readerLock && !paidLock && !methodPicked ? undefined
          : !readerLock && (paymentMethod === 'CARD_UNSET' || readerGone || autoCardStale) ? POS_CARD_STEP.pending
            : cashShort ? describeCashShort(cash) : undefined}
      />
      {cancelDialog}
    </div>;
  }

  // VENTA COMPLETADA: solo con una venta que el servidor devolvió completada. Se queda hasta que la
  // persona toca «Nueva venta»: no hay regreso automático.
  if (completedSale) {
    return (
      <PosDone
        firstSale={isFirstSale ? { onGoHome: () => { window.location.hash = 'dashboard'; } } : undefined}
        onNewSale={() => setLastSale(undefined)}
        onOpenDetail={completedSale.otherSaleId ? () => onOpenDetail(completedSale.otherSaleId!) : undefined}
        sale={completedSale}
      />
    );
  }
  // El ciclo se cerró con una venta que NO está completada: se dice su estado real, sin festejar.
  const unfinishedOutcome = lastSale ? describeSaleOutcome(lastSale) : undefined;

  const catalogState = posCatalogState(storeId, products, productsError);
  // La tienda del flujo. Un empleado vende siempre en la suya: no la elige.
  const storeName = isEmployee ? (stores[0]?.name ?? 'Sin tienda') : (stores.find((store) => store.id === storeId)?.name ?? '');
  const showSheet = orderOpen && !split;

  const checkoutErrorNotice = checkoutError && (
    <PosNotice
      actions={checkoutError.ambiguous ? <>
        <Button onClick={onGoToHistory} variant="secondary">Revisar Historial</Button>
        <Button disabled={isSaving} onClick={() => void startCheckout()} variant="secondary">Reintentar de forma segura</Button>
      </> : undefined}
      role="alert"
      tone="error"
    >
      {checkoutError.message}
    </PosNotice>
  );
  const orderLines = (
    <PosOrderLines
      busy={isSaving}
      cart={cart}
      onClear={() => setConfirmingClear(true)}
      onDecrease={decLine}
      onIncrease={incLine}
      onRemove={removeLine}
    />
  );
  // El método de pago se elige DESPUÉS, en la pantalla de cobro: primero se guarda la venta, para
  // que nada se pierda si algo falla al cobrar. Deuda (k): cancelar desde la lista de pendientes
  // pone `isSaving`; «Guardando…» solo se dice cuando de verdad se está guardando.
  const orderFoot = <PosOrderFoot disabled={checkoutDisabled} onCheckout={() => void startCheckout()} preview={preview} saving={isSaving && !isCanceling} sheet={!split} />;

  return <div className={keyboardOpen ? 'n3-pos--keyboard' : undefined} ref={posRoot}>
    <section aria-labelledby="pos-title" className="n3-pos n3-pos--sale" ref={setSaleRoot} tabIndex={-1}>
      <div className="n3-pos__stack">
        <PosHeader
          businessName={business?.name || 'Negocio'}
          disabled={isSaving}
          links={viewLinks}
          onStoreSelect={handleStoreSelect}
          storeId={storeId}
          storeLocked={isEmployee}
          storeName={storeName}
          stores={stores}
        />

        {loyverseTip && (
          <PosNotice onDismiss={loyverseTip.onDismiss} role="status" tone="warning">
            Registra aquí solo ventas que <strong>no</strong> estén en Loyverse (para no duplicar).
          </PosNotice>
        )}

        {lastSale && unfinishedOutcome && (
          <PosNotice
            actions={lastSale.otherSaleId ? <Button onClick={() => onOpenDetail(lastSale.otherSaleId!)} variant="secondary">Ver detalle</Button> : undefined}
            onDismiss={() => setLastSale(undefined)}
            role="status"
            title={`${unfinishedOutcome.title} · ${formatMoney(lastSale.totalAmount)}`}
            tone={unfinishedOutcome.tone}
          >
            {unfinishedOutcome.detail}
          </PosNotice>
        )}

        {/* Deuda (k): cancelar tiene que VERSE. Con el cliente enfrente, «el panel se cerró» no es una
            respuesta. Va ARRIBA de las ventas pendientes para no esconderlas. */}
        {canceledNotice && (
          <PosNotice onDismiss={() => setCanceledNotice(undefined)} role="status" title={`${canceledNotice.title} · ${formatMoney(canceledNotice.totalAmount)}`} tone="info">
            {canceledNotice.detail}
          </PosNotice>
        )}

        {/* Recuperación: ventas que quedaron guardadas sin cobrar (recarga, cierre de app, sin señal).
            Deuda (r): el subtítulo no afirma «no se registró ningún cobro» cuando alguna de estas ventas
            intentó cobrarse con el lector, y la marca por fila dice de cuál se habla. */}
        {listedRecoverable.length > 0 && (
          <PosRecovery
            atRisk={recovery.atRisk}
            busy={isSaving}
            // Lote 5: la salida de una venta cuyo cobro el servidor no pudo verificar. Solo el DUEÑO —el
            // servidor responde 403 a cualquier otro— y solo cuando el cobro está EN VILO según el
            // servidor, que es el mismo criterio que la guarda usa para aceptar una declaración.
            canDeclare={(sale) => canDeclareNativeSaleUnpaid(user?.role) && hasReaderPaymentInFlight(sale.payments)}
            detail={recovery.detail}
            flag={recovery.flag}
            onCancel={(sale) => setCancelTarget(sale)}
            // Se limpia al abrir: si no, el texto escrito para una venta seguía vivo al abrir otra.
            onDeclare={(sale) => { setDeclaring(sale); setDeclarationBasis(''); setDeclarationError(undefined); }}
            // Deuda (q): «Continuar» PREGUNTA al servidor antes de pintar la pantalla de cobro.
            onResume={(sale) => void resumeSale(sale)}
            // FI-53: las que ya tienen el cobro confirmado llevan su marca y no ofrecen «Cancelar».
            paid={recovery.paid}
            paidFlag={recovery.paidFlag}
            resumingSaleId={resumingSaleId}
            sales={listedRecoverable}
            // La zona es la de la TIENDA DE ESA VENTA, no la seleccionada en pantalla: una venta
            // pendiente puede ser de otra sucursal. Así esta hora coincide con la del Historial y con
            // la del proveedor.
            timeOf={(sale) => shortTime(sale.createdAt, resolveStoreTimezone(stores, { storeId: sale.storeId }))}
          />
        )}

        {/* Con el pedido abierto como hoja, el error va dentro de ella: detrás no se vería. Fuera de
            ella, un error nuevo se trae a la vista (P-080: «Cobrar» también está en la barra). */}
        {!showSheet && checkoutErrorNotice && <div className="n3-pos__sale-error" ref={saleErrorBox}>{checkoutErrorNotice}</div>}

        <div className="n3-pos__layout">
          {/* P0: el estado vacío solo se afirma con tienda resuelta + respuesta real del catálogo. */}
          <PosCatalog
            busy={isSaving}
            canManageCatalog={canManageCatalog}
            cart={cart}
            error={productsError}
            onAdd={addToCart}
            onGoToCatalog={onGoToCatalog}
            onQuery={setQuery}
            onView={chooseCatalogView}
            onRetry={() => void loadProducts(storeId)}
            products={products}
            query={query}
            state={catalogState}
            view={catalogView}
          />
          {split && (
            <aside aria-labelledby="pos-order-title" className="n3-pos__panel-order" ref={setOrderPanel} style={orderPanelRoom === undefined ? undefined : { maxHeight: orderPanelRoom }}>
              <h2 className="n3-pos__panel-order-title" id="pos-order-title">Pedido ({cart.length})</h2>
              <div className="n3-pos__panel-order-body">{orderLines}</div>
              {orderFoot}
            </aside>
          )}
        </div>
      </div>

      {/* Vacía, la barra invita a tocar un producto: solo se dibuja cuando hay productos que tocar.
          P-080: «Cobrar» de la barra es el de la hoja —misma función, mismo apagado, misma espera—. */}
      {!split && (!isCartEmpty(cart) || catalogState === 'ready') && (
        <PosOrderBar
          cart={cart}
          disabled={checkoutDisabled}
          onCheckout={() => void startCheckout()}
          onOpen={() => setOrderOpen(true)}
          saving={isSaving && !isCanceling}
          total={preview.totalAmount}
        />
      )}
    </section>

    {showSheet && (
      <Dialog footer={orderFoot} isBusy={isSaving} onClose={() => setOrderOpen(false)} title={`Pedido (${cart.length})`}>
        {checkoutErrorNotice}
        {orderLines}
        {!isCartEmpty(cart) && <PosOrderEstimate />}
      </Dialog>
    )}

    {confirmingClear && (
      <Dialog
        footer={<>
          <Button onClick={() => setConfirmingClear(false)} variant="secondary">No, regresar</Button>
          <Button className="n3-pos__danger" onClick={() => { clearCart(); setConfirmingClear(false); }} variant="primary">Sí, vaciar</Button>
        </>}
        onClose={() => setConfirmingClear(false)}
        title="¿Vaciar el pedido?"
      >
        <p className="n3-pos__dialog-text">{cartItemCount(cart) === 1 ? 'Se quitará' : 'Se quitarán'} {describeUnits(cartItemCount(cart))} del pedido.</p>
      </Dialog>
    )}

    {cancelDialog}

    {declaring && (() => {
      const copy = describeDeclareUnpaid(declaring, resolveStoreTimezone(stores, { storeId: declaring.storeId }));
      const ready = declarationBasis.trim().length >= 10;
      return <Dialog
        footer={<>
          <Button disabled={isDeclaring} onClick={() => setDeclaring(undefined)} variant="secondary">{copy.cancel}</Button>
          <Button disabled={isDeclaring || !ready} onClick={() => void confirmDeclaration()} variant="primary">
            {isDeclaring ? 'Cerrando…' : copy.confirm}
          </Button>
        </>}
        isBusy={isDeclaring}
        onClose={() => setDeclaring(undefined)}
        title={copy.title}
      >
        {declarationError && <PosNotice role="alert" tone="error">{declarationError}</PosNotice>}
        <p className="n3-pos__dialog-text"><strong>{copy.saleLine}</strong></p>
        <p className="n3-pos__dialog-text">{copy.why}</p>
        <Field hint={copy.basisHint} label={copy.basisLabel}>
          <Textarea
            onChange={(event) => setDeclarationBasis(event.target.value)}
            placeholder={DECLARE_UNPAID_BASIS_SUGGESTION}
            rows={5}
            value={declarationBasis}
          />
        </Field>
        <p className="n3-pos__dialog-text"><strong>{copy.commitment}</strong></p>
      </Dialog>;
    })()}

    {pendingStoreId && (
      <Dialog
        footer={<>
          <Button onClick={() => setPendingStoreId(undefined)} variant="secondary">Cancelar</Button>
          <Button onClick={() => applyStoreChange(pendingStoreId)} variant="primary">Vaciar pedido y cambiar</Button>
        </>}
        onClose={() => setPendingStoreId(undefined)}
        title="Cambiar de tienda"
      >
        <p className="n3-pos__dialog-text">
          Cambiar a <strong>{stores.find((store) => store.id === pendingStoreId)?.name ?? 'otra tienda'}</strong> vaciará el pedido actual ({describeUnits(cartItemCount(cart))}). ¿Continuar?
        </p>
      </Dialog>
    )}
  </div>;
}

/** Por qué «Confirmar cobro» está apagado con Efectivo: lo escrito está mal escrito o no alcanza (vacío sí se puede). */
function describeCashShort(cash: PosCashReceived): string {
  if (cash.kind === 'short') return `Lo recibido no alcanza: faltan ${formatMoney(cash.missing)}.`;
  return POS_CASH.whyInvalid;
}

/** La dirección del punto de venta (P-075): la guarda del navegador solo vive y actúa en ella. */
const POS_ROUTE = 'other-sales/vender';

/** El aire que queda arriba del error de «Cobrar» al traerlo a la vista (el mismo del cobro). */
const SALE_ERROR_MARGIN = 12;

/** El almacenamiento del navegador, si se deja usar (en modo privado puede negarse, incluso al pedirlo). */
function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

type PosScreen = 'sale' | 'checkout' | 'done';

/** El título de cada pantalla: a su sección va el foco al llegar. */
const SCREEN_TITLE: Record<PosScreen, string> = { sale: 'pos-title', checkout: 'pos-checkout-title', done: 'pos-done-title' };

const NO_VIEW_LINKS: PosViewLink[] = [];
